import { useState, useRef, useCallback, useEffect } from "react";
import { useDropzone, DropzoneOptions } from "react-dropzone";
import { ToastQueue } from '@adobe/react-spectrum';
import { useDependency } from "../../contexts/DependencyProvider";
import { CreateDocumentInStorageRequest } from "../../services/soap/document_library/requests/CreateDocumentInStorageRequest";

/**
 * File item interface for attachment management
 */
export interface FileItem {
  id: number;
  fileName: string;
  fileSize: number;
  base64Files: string;
  /** Original attachment data if from existing message */
  originalData?: any;
}

/**
 * Uploaded document result
 */
export interface UploadedDocument {
  id: string;
  name: string;
  size: number;
  originalId?: number;
}

/** Maximum file size in bytes (20MB) */
export const MAX_FILE_SIZE = 20 * 1024 * 1024;

/** Default accepted file types */
export const DEFAULT_ACCEPTED_FILES: DropzoneOptions["accept"] = {
  "image/*": [],
  "application/pdf": [],
  "application/msword": [],
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document": [],
  "application/vnd.ms-excel": [],
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [],
  "text/*": [],
};

interface UseNewMessageFilesOptions {
  /** Context ID for document storage */
  contextId: any;
  /** Optional fallback tree handle for message attachment uploads */
  fallbackTreeHandle?: any;
  /** Initial attachments (e.g., from email reply) */
  initialAttachments?: any[];
  /** Custom dropzone options */
  dropzoneOptions?: Partial<DropzoneOptions>;
}

/**
 * Convert file to base64 string
 */
const getBase64 = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = () =>
      reject(new Error(`Failed to read file: ${file.name}`));
  });
};

/**
 * Format file size for display
 */
export const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return "0 Bytes";
  const k = 1024;
  const sizes = ["Bytes", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
};

/**
 * Hook for managing file attachments in new message dialogs
 * Handles file selection, drag-drop, upload to server, and deletion
 */
export const useNewMessageFiles = ({
  contextId,
  fallbackTreeHandle,
  initialAttachments = [],
  dropzoneOptions = {},
}: UseNewMessageFilesOptions) => {
  const { documentLibraryService, store } = useDependency();

  const [fileList, setFileList] = useState<FileItem[]>([]);
  const fileListRef = useRef<FileItem[]>([]);
  const itemCounter = useRef<number>(1);
  const [isUploading, setIsUploading] = useState(false);

  // Keep ref in sync with state
  useEffect(() => {
    fileListRef.current = fileList;
  }, [fileList]);

  // Initialize from initial attachments
  useEffect(() => {
    if (initialAttachments?.length > 0) {
      const mappedAttachments: FileItem[] = initialAttachments.map(
        (att: any, index: number) => ({
          id: itemCounter.current++,
          fileName: att.fileName || att.name || `attachment_${index}`,
          fileSize: att.fileSize || att.size || 0,
          base64Files: att.base64Files || att.data || "",
          originalData: att,
        }),
      );
      setFileList(mappedAttachments);
    }
  }, []);

  /**
   * Process files and convert to base64
   */
  const handleFilesAsync = useCallback(async (files: File[]): Promise<void> => {
    const newFiles: FileItem[] = [];
    const errors: string[] = [];

    for (const file of Array.from(files)) {
      try {
        // Validate file size
        if (file.size > MAX_FILE_SIZE) {
          errors.push(
            `${file.name} is too large (max ${formatFileSize(MAX_FILE_SIZE)})`,
          );
          continue;
        }

        const base64Data = await getBase64(file);
        newFiles.push({
          id: itemCounter.current++,
          fileName: file.name,
          fileSize: file.size,
          base64Files: base64Data,
        });
      } catch (error) {
        console.error(`Error processing file ${file.name}:`, error);
        errors.push(`Failed to process ${file.name}`);
      }
    }

    // Show errors if any
    if (errors.length > 0) {
      ToastQueue.negative(errors.join("\n"), { timeout: 5000 });
    }

    // Add new files to list
    if (newFiles.length > 0) {
      setFileList((prev) => [...prev, ...newFiles]);
    }
  }, []);

  /**
   * Handle file drop
   */
  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      const processFiles = async () => {
        try {
          await handleFilesAsync(acceptedFiles);
        } catch (err) {
          console.error("Error in file drop:", err);
          ToastQueue.negative("Error processing files", { timeout: 3000 });
        }
      };
      processFiles();
    },
    [handleFilesAsync],
  );

  /**
   * Delete file from list
   */
  const deleteFile = useCallback((id: number) => {
    setFileList((prev) => prev.filter((item) => item.id !== id));
  }, []);

  /**
   * Clear all files
   */
  const clearFiles = useCallback(() => {
    setFileList([]);
    itemCounter.current = 1;
  }, []);

  /**
   * Upload all files to server storage
   * @returns Array of uploaded document info
   */
  const uploadFiles = useCallback(async (): Promise<UploadedDocument[]> => {
    if (fileListRef.current.length === 0) {
      return [];
    }

    setIsUploading(true);
    const uploadedDocuments: UploadedDocument[] = [];
    const errors: string[] = [];
    const effectiveTreeHandle = String(
      fallbackTreeHandle || contextId || ""
    ).trim();

    try {
      const uploadPromises = fileListRef.current.map(
        async (item): Promise<UploadedDocument | null> => {
          try {
            // Existing server attachment (e.g. from reply/forward) — already uploaded, return its ID directly
            if (!item?.base64Files && item?.originalData) {
              const serverId =
                item.originalData.id ||
                item.originalData.docId ||
                item.originalData.documentId;
              if (serverId) {
                return {
                  id: String(serverId),
                  name: item.fileName,
                  size: item.fileSize,
                  originalId: item.id,
                };
              }
              // No usable server ID — skip silently
              return null;
            }

            // Validate item
            if (!item?.fileName || !item?.base64Files) {
              console.error("Invalid file object:", item);
              return null;
            }

            // Validate size
            if (item.fileSize > MAX_FILE_SIZE) {
              errors.push(
                `${item.fileName} is too large (max ${formatFileSize(
                  MAX_FILE_SIZE,
                )})`,
              );
              return null;
            }

            // Extract base64 data (string base64, data URL, or ArrayBuffer from Print dialog)
            let data: string;
            let effectiveFileSize = item.fileSize;
            const raw = item.base64Files as string | ArrayBuffer;
            if (typeof raw === "string") {
              data = raw.includes(",") ? raw.split(",")[1] : raw;
            } else if (raw instanceof ArrayBuffer) {
              // Print dialog passes ArrayBuffer from printContext (REST arraybuffer response)
              const bytes = new Uint8Array(raw);
              effectiveFileSize = effectiveFileSize || bytes.length;
              const chunkSize = 8192;
              let binary = "";
              for (let i = 0; i < bytes.length; i += chunkSize) {
                const chunk = bytes.subarray(
                  i,
                  Math.min(i + chunkSize, bytes.length),
                );
                binary += String.fromCharCode(...chunk);
              }
              data = btoa(binary);
            } else {
              console.error(`Unsupported data type for ${item.fileName}`);
              return null;
            }

            // Validate base64
            if (!data || !data.match(/^[A-Za-z0-9+/]*={0,2}$/)) {
              errors.push(`Invalid file format: ${item.fileName}`);
              return null;
            }

            if (!effectiveTreeHandle) {
              errors.push(`No valid treeHandle found for ${item.fileName}`);
              return null;
            }

            const request = new CreateDocumentInStorageRequest(
              store.Server,
              store.SessionId,
              {
                id: "",
                acl: "",
                parent: "",
                name: item.fileName,
                description: `Uploaded via message dialog - ${new Date().toISOString()}`,
                location: "",
                validByDate: "0",
                fileHandle: "",
                treeHandle: effectiveTreeHandle,
                fileSize: String(effectiveFileSize),
                internalNumber: "",
                fileName: item.fileName,
                deleted: "0",
                validFrom: new Date(),
                validTo: new Date(),
                storageId: "",
                isInStorage: "0",
                contextId: contextId,
              },
              data,
            );

            const result =
              await documentLibraryService.createDocumentInStorage(request);

            if (result?.result === "OK" && result.newId) {
              return {
                id: String(result.newId),
                name: item.fileName,
                size: effectiveFileSize,
                originalId: item.id,
              };
            } else {
              const errorMessage =
                result?.EXCEPTION?.reason ||
                result?.EXCEPTION?.message ||
                "Unknown error";
              errors.push(`Failed to upload ${item.fileName}: ${errorMessage}`);
              return null;
            }
          } catch (error: any) {
            console.error(`Error uploading ${item.fileName}:`, error);
            errors.push(
              `Error uploading ${item.fileName}: ${
                error.message || "Unknown error"
              }`,
            );
            return null;
          }
        },
      );

      const results = await Promise.all(uploadPromises);

      // Filter successful uploads
      results.forEach((result) => {
        if (result) {
          uploadedDocuments.push(result);
        }
      });

      // Show errors if any
      if (errors.length > 0) {
        ToastQueue.negative(errors.join("\n"), { timeout: 5000 });
      }

      return uploadedDocuments;
    } finally {
      setIsUploading(false);
    }
  }, [
    contextId,
    documentLibraryService,
    fallbackTreeHandle,
    store.Server,
    store.SessionId,
  ]);

  // Configure dropzone
  const {
    getRootProps,
    getInputProps,
    isDragActive,
    open: openFilePicker,
  } = useDropzone({
    onDrop,
    noClick: true,
    noKeyboard: true,
    noDragEventsBubbling: true,
    preventDropOnDocument: true,
    accept: DEFAULT_ACCEPTED_FILES,
    ...dropzoneOptions,
  });

  return {
    // State
    fileList,
    fileListRef,
    isUploading,
    isDragActive,

    // Actions
    handleFilesAsync,
    deleteFile,
    clearFiles,
    uploadFiles,
    setFileList,
    openFilePicker,

    // Dropzone props
    getRootProps,
    getInputProps,
  };
};
